<template>
  <div class="settings-page">
    <!-- Background Elements -->
    <div class="page-grid"></div>
    <div class="page-orb orb-1"></div>
    <div class="page-orb orb-2"></div>

    <div class="settings-container">
      <!-- Header -->
      <div class="settings-header">
        <div class="header-content">
          <h1 class="header-title">
            Manage My Settings
          </h1>
          <p class="header-subtitle">Personalize your experience and keep your profile up to date.</p>
        </div>
        <button class="btn-back" @click="$router.push('/staff-dashboard')">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Back to Dashboard
        </button>
      </div>

      <div class="settings-grid">
        <!-- LEFT SIDEBAR -->
        <div class="sidebar-card">
          <!-- Avatar Section -->
          <div class="avatar-section">
            <div class="avatar-wrapper">
              <div v-if="photoURL" class="avatar" :style="{ backgroundImage: `url(${photoURL})` }"></div>
              <div v-else class="avatar avatar-placeholder" :style="{ background: avatarGradient }">
                {{ initials }}
              </div>
              <button class="avatar-edit" @click="$refs.avatarInput.click()">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M11.3333 2.00001C11.5084 1.82491 11.716 1.68602 11.9441 1.59126C12.1722 1.49649 12.4165 1.44769 12.6633 1.44769C12.9101 1.44769 13.1544 1.49649 13.3825 1.59126C13.6106 1.68602 13.8183 1.82491 13.9933 2.00001C14.1684 2.17511 14.3073 2.38276 14.4021 2.61088C14.4968 2.83899 14.5456 3.08329 14.5456 3.33001C14.5456 3.57673 14.4968 3.82103 14.4021 4.04914C14.3073 4.27726 14.1684 4.48491 13.9933 4.66001L5.00001 13.6533L1.33334 14.6667L2.34668 11L11.3333 2.00001Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              <input
                ref="avatarInput"
                type="file"
                accept="image/*"
                @change="updateAvatar"
                style="display: none"
              />
            </div>
            <div class="user-info">
              <h2 class="user-name">
                {{ displayName || 'User' }}
              </h2>
              <p class="user-email">{{ email }}</p>
            </div>
          </div>

          <!-- Points Card -->
          <div class="points-card">
            <div class="points-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="points-content">
              <div class="points-label">Your Points</div>
              <div class="points-value">{{ points }}</div>
              <div class="points-today">+{{ pointsToday }} today</div>
              <div class="points-progress">
                <div class="progress-bar">
                  <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
                </div>
                <div class="progress-label">Next badge in {{ pointsToNextBadge }} pts</div>
              </div>
            </div>
          </div>

          <!-- Activity Card -->
          <div class="activity-card">
            <div class="activity-header">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M2.25 15.75L6.75 9.75L10.125 12.375L15.75 5.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M11.25 5.25H15.75V9.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>Your Activity</span>
            </div>

            <div v-if="activityLoaded && totalActivity === 0" class="activity-empty">
              Log a call, email, or meeting to see your activity here.
            </div>

            <template v-else>
              <div class="activity-chart">
                <svg viewBox="0 0 280 80" width="100%" height="80" preserveAspectRatio="none">
                  <polyline
                    :points="activityPoints"
                    fill="none"
                    stroke="#C89B4C"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>

              <div class="activity-stats">
                <div class="stat-item">
                  <span class="stat-value">{{ stats.calls }}</span>
                  <span class="stat-label">Calls</span>
                </div>
                <span class="stat-divider">·</span>
                <div class="stat-item">
                  <span class="stat-value">{{ stats.emails }}</span>
                  <span class="stat-label">Emails</span>
                </div>
                <span class="stat-divider">·</span>
                <div class="stat-item">
                  <span class="stat-value">{{ stats.meetings }}</span>
                  <span class="stat-label">Meetings</span>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- RIGHT CONTENT -->
        <div class="content-card">
          <!-- Tabs -->
          <div class="tabs-header">
            <button
              v-for="(tabItem, idx) in tabs"
              :key="idx"
              :class="['tab-button', { active: activeTab === idx }]"
              @click="activeTab = idx"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" v-html="tabItem.icon"></svg>
              <span>{{ tabItem.label }}</span>
            </button>
          </div>

          <!-- Tab Content -->
          <div class="tab-content">
            <!-- PROFILE TAB -->
            <div v-if="activeTab === 0" class="tab-panel">
              <form @submit.prevent="saveProfile" class="settings-form">
                <div class="form-group">
                  <label>Full Name</label>
                  <input
                    v-model="profileForm.displayName"
                    type="text"
                    placeholder="Enter your full name"
                  />
                </div>

                <div class="form-group">
                  <label>Email</label>
                  <input
                    v-model="profileForm.email"
                    type="email"
                    disabled
                  />
                  <p class="form-hint">Email cannot be changed</p>
                </div>

                <div class="form-group">
                  <label>Phone Number</label>
                  <input
                    v-model="profileForm.phone"
                    type="tel"
                    placeholder="Enter your phone number"
                  />
                </div>

                <div class="form-group">
                  <label>Address</label>
                  <textarea
                    v-model="profileForm.address"
                    rows="3"
                    placeholder="Enter your address"
                  ></textarea>
                </div>

                <button type="submit" class="btn-save" :disabled="savingProfile">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M15 2.5H5C4.17157 2.5 3.5 3.17157 3.5 4V16C3.5 16.8284 4.17157 17.5 5 17.5H15C15.8284 17.5 16.5 16.8284 16.5 16V4C16.5 3.17157 15.8284 2.5 15 2.5Z" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M12.5 2.5V6.5H7.5V2.5" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M7.5 11H12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                    <path d="M7.5 14H12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                  {{ savingProfile ? 'Saving...' : 'Save Changes' }}
                </button>
              </form>
            </div>

            <!-- SECURITY TAB -->
            <div v-if="activeTab === 1" class="tab-panel">
              <form @submit.prevent="changePassword" class="settings-form">
                <div class="security-notice">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <div>
                    <div class="notice-title">Secure Your Account</div>
                    <div class="notice-text">Keep your password strong and unique</div>
                  </div>
                </div>

                <div class="form-group">
                  <label>Current Password</label>
                  <div class="password-input">
                    <input
                      v-model="passwordForm.currentPassword"
                      :type="showCurrentPassword ? 'text' : 'password'"
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      class="btn-toggle-password"
                      @click="showCurrentPassword = !showCurrentPassword"
                    >
                      <svg v-if="showCurrentPassword" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M2.5 10C2.5 10 5 4.16666 10 4.16666C15 4.16666 17.5 10 17.5 10C17.5 10 15 15.8333 10 15.8333C5 15.8333 2.5 10 2.5 10Z" stroke="currentColor" stroke-width="1.5"/>
                        <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" stroke-width="1.5"/>
                      </svg>
                      <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M14.95 14.95C13.5255 16.0358 11.7909 16.6374 10 16.6667C5 16.6667 2.5 10.8333 2.5 10.8333C3.35906 9.06829 4.52523 7.48565 5.94166 6.16666M8.25 4.53332C8.82365 4.39904 9.41093 4.33189 10 4.33332C15 4.33332 17.5 10.1667 17.5 10.1667C17.0125 11.0746 16.4208 11.9282 15.7333 12.7167M11.7667 11.7667C11.5378 12.0123 11.2617 12.2093 10.9551 12.3459C10.6485 12.4826 10.3176 12.556 9.98208 12.5619C9.64652 12.5678 9.31332 12.506 9.00185 12.3804C8.69037 12.2547 8.40721 12.0676 8.16937 11.8298C7.93153 11.5919 7.7444 11.3088 7.61876 10.9973C7.49312 10.6858 7.43128 10.3526 7.43718 10.0171C7.44307 9.68152 7.51658 9.3506 7.65325 9.04397C7.78992 8.73735 7.98691 8.46125 8.23249 8.23232" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M2.5 2.5L17.5 17.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                      </svg>
                    </button>
                  </div>
                  <p class="form-hint">
                    Don't know it? <router-link to="/login" class="forgot-link">Use "Forgot password?" on the sign-in page</router-link> to reset it by email instead.
                  </p>
                </div>

                <div class="form-group">
                  <label>New Password</label>
                  <div class="password-input">
                    <input
                      v-model="passwordForm.newPassword"
                      :type="showNewPassword ? 'text' : 'password'"
                      placeholder="Enter new password"
                      @input="checkPasswordStrength"
                    />
                    <button
                      type="button"
                      class="btn-toggle-password"
                      @click="showNewPassword = !showNewPassword"
                    >
                      <svg v-if="showNewPassword" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M2.5 10C2.5 10 5 4.16666 10 4.16666C15 4.16666 17.5 10 17.5 10C17.5 10 15 15.8333 10 15.8333C5 15.8333 2.5 10 2.5 10Z" stroke="currentColor" stroke-width="1.5"/>
                        <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" stroke-width="1.5"/>
                      </svg>
                      <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M14.95 14.95C13.5255 16.0358 11.7909 16.6374 10 16.6667C5 16.6667 2.5 10.8333 2.5 10.8333C3.35906 9.06829 4.52523 7.48565 5.94166 6.16666M8.25 4.53332C8.82365 4.39904 9.41093 4.33189 10 4.33332C15 4.33332 17.5 10.1667 17.5 10.1667C17.0125 11.0746 16.4208 11.9282 15.7333 12.7167M11.7667 11.7667C11.5378 12.0123 11.2617 12.2093 10.9551 12.3459C10.6485 12.4826 10.3176 12.556 9.98208 12.5619C9.64652 12.5678 9.31332 12.506 9.00185 12.3804C8.69037 12.2547 8.40721 12.0676 8.16937 11.8298C7.93153 11.5919 7.7444 11.3088 7.61876 10.9973C7.49312 10.6858 7.43128 10.3526 7.43718 10.0171C7.44307 9.68152 7.51658 9.3506 7.65325 9.04397C7.78992 8.73735 7.98691 8.46125 8.23249 8.23232" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M2.5 2.5L17.5 17.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                      </svg>
                    </button>
                  </div>
                  <div v-if="passwordForm.newPassword" class="password-strength">
                    <div class="strength-bar">
                      <div
                        class="strength-fill"
                        :style="{ width: passwordStrength.percent + '%', background: passwordStrength.color }"
                      ></div>
                    </div>
                    <span class="strength-label" :style="{ color: passwordStrength.color }">
                      {{ passwordStrength.label }}
                    </span>
                  </div>
                </div>

                <div class="form-group">
                  <label>Confirm New Password</label>
                  <input
                    v-model="passwordForm.confirmPassword"
                    :type="showNewPassword ? 'text' : 'password'"
                    placeholder="Confirm new password"
                  />
                </div>

                <div v-if="passwordError" class="form-error">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" stroke="currentColor" stroke-width="1.5"/>
                    <path d="M8 5V8.5M8 11H8.005" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                  {{ passwordError }}
                </div>

                <button type="submit" class="btn-save" :disabled="changingPassword">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M15 8.75V6.25C15 5.91848 14.8683 5.60054 14.6339 5.36612C14.3995 5.1317 14.0815 5 13.75 5H6.25C5.91848 5 5.60054 5.1317 5.36612 5.36612C5.1317 5.60054 5 5.91848 5 6.25V13.75C5 14.0815 5.1317 14.3995 5.36612 14.6339C5.60054 14.8683 5.91848 15 6.25 15H8.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M10 10H16.25M16.25 10L14.375 8.125M16.25 10L14.375 11.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  {{ changingPassword ? 'Changing...' : 'Change Password' }}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <div v-if="snack.show" class="toast-notification">
      <span>{{ snack.msg }}</span>
      <button @click="snack.show = false">✕</button>
    </div>
  </div>
</template>

<script>
import { auth, db, storage } from '@/firebase'
import { updateProfile, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth'
import { doc, getDoc, setDoc, onSnapshot, collectionGroup, query, where, getDocs } from 'firebase/firestore'
import { ref as sRef, uploadBytes, getDownloadURL } from 'firebase/storage'

export default {
  name: 'StaffSettings',
  data() {
    return {
      activeTab: 0,
      tabs: [
        {
          label: 'Profile',
          icon: '<path d="M10 11.6667C12.7614 11.6667 15 9.42811 15 6.66667C15 3.90524 12.7614 1.66667 10 1.66667C7.23858 1.66667 5 3.90524 5 6.66667C5 9.42811 7.23858 11.6667 10 11.6667Z" stroke="currentColor" stroke-width="1.5"/><path d="M3.33334 18.3333C3.33334 16.5652 4.03572 14.8695 5.28596 13.6193C6.5362 12.369 8.23189 11.6667 10 11.6667C11.7681 11.6667 13.4638 12.369 14.7141 13.6193C15.9643 14.8695 16.6667 16.5652 16.6667 18.3333" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>'
        },
        {
          label: 'Security',
          icon: '<path d="M10 18.3333C10 18.3333 16.6667 15 16.6667 10V4.16667L10 1.66667L3.33334 4.16667V10C3.33334 15 10 18.3333 10 18.3333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>'
        }
      ],

      // User data
      email: '',
      displayName: '',
      photoURL: '',

      // Live points/streak data from the `user_stats` collection (same
      // source of truth used by the leads view)
      userStats: {
        points: 0,
        todayPoints: 0,
        streakDays: 0,
        lastActionDate: null
      },
      unsubStats: null,
      badgeTiers: [
        { threshold: 100, label: 'Rookie' },
        { threshold: 500, label: 'Closer' },
        { threshold: 1500, label: 'Rainmaker' },
        { threshold: 3000, label: 'Elite' }
      ],

      // Profile form
      profileForm: {
        displayName: '',
        email: '',
        phone: '',
        address: ''
      },
      savingProfile: false,

      // Password form
      passwordForm: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      showCurrentPassword: false,
      showNewPassword: false,
      changingPassword: false,
      passwordError: '',
      passwordStrength: {
        percent: 0,
        label: '',
        color: ''
      },

      // Activity data — populated from the `interactions` subcollections
      // logged against leads (see loadActivityStats)
      activityData: [],
      activityLoaded: false,
      stats: {
        calls: 0,
        emails: 0,
        meetings: 0
      },

      // UI
      snack: { show: false, msg: '' }
    }
  },

  computed: {
    initials() {
      return this.displayName
        .split(' ')
        .map(n => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    },
    avatarGradient() {
      return 'linear-gradient(135deg, #1E3733, #2F4F4A)'
    },
    points() {
      return this.userStats.points || 0
    },
    pointsToday() {
      const todayKey = new Date().toDateString()
      return this.userStats.lastActionDate === todayKey ? (this.userStats.todayPoints || 0) : 0
    },
    nextBadgeTier() {
      return this.badgeTiers.find(tier => this.points < tier.threshold) || null
    },
    progressPercent() {
      if (!this.nextBadgeTier) return 100
      const prevThreshold = [...this.badgeTiers]
        .reverse()
        .find(tier => tier.threshold <= this.points)?.threshold || 0
      const span = this.nextBadgeTier.threshold - prevThreshold
      const progressed = this.points - prevThreshold
      return Math.min(100, Math.round((progressed / span) * 100))
    },
    pointsToNextBadge() {
      if (!this.nextBadgeTier) return 0
      return this.nextBadgeTier.threshold - this.points
    },
    totalActivity() {
      return this.stats.calls + this.stats.emails + this.stats.meetings
    },
    activityPoints() {
      const width = 280
      const height = 80
      const padding = 10
      const maxVal = Math.max(1, ...this.activityData)
      const points = this.activityData.map((val, i) => {
        const x = (i / (this.activityData.length - 1)) * (width - padding * 2) + padding
        const y = height - ((val / maxVal) * (height - padding * 2)) - padding
        return `${x},${y}`
      })
      return points.join(' ')
    }
  },

  async created() {
    const user = auth.currentUser
    if (!user) {
      this.$router.push('/')
      return
    }

    this.email = user.email
    this.displayName = user.displayName || 'User'
    this.photoURL = user.photoURL || ''

    this.profileForm.displayName = this.displayName
    this.profileForm.email = this.email

    // Load additional user data from Firestore
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid))
      if (userDoc.exists()) {
        const data = userDoc.data()
        this.profileForm.phone = data.phone || ''
        this.profileForm.address = data.address || ''
      }
    } catch (e) {
      console.error('Error loading user data:', e)
    }

    // Live points/streak, same source of truth as the leads view
    const statsRef = doc(db, 'user_stats', user.uid)
    this.unsubStats = onSnapshot(statsRef, snap => {
      if (snap.exists()) {
        this.userStats = Object.assign({
          points: 0,
          todayPoints: 0,
          streakDays: 0,
          lastActionDate: null
        }, snap.data())
      }
    })

    this.loadActivityStats()
  },

  beforeUnmount() {
    if (this.unsubStats) this.unsubStats()
  },

  methods: {
    async loadActivityStats() {
      const user = auth.currentUser
      if (!user) return

      try {
        const interactionsQuery = query(
          collectionGroup(db, 'interactions'),
          where('by', '==', user.uid)
        )
        const snap = await getDocs(interactionsQuery)

        const counts = { call: 0, email: 0, meeting: 0 }

        // Last 14 days, oldest first, for the sparkline
        const now = new Date()
        const dayKeys = []
        const dayBuckets = {}
        for (let i = 13; i >= 0; i--) {
          const key = new Date(now.getTime() - i * 24 * 60 * 60 * 1000).toDateString()
          dayKeys.push(key)
          dayBuckets[key] = 0
        }

        snap.forEach(docSnap => {
          const data = docSnap.data()
          if (counts[data.type] !== undefined) counts[data.type]++

          const ts = data.at
          if (ts) {
            const date = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000)
            const key = date.toDateString()
            if (dayBuckets[key] !== undefined) dayBuckets[key]++
          }
        })

        this.stats = counts
        this.activityData = dayKeys.map(key => dayBuckets[key])
      } catch (e) {
        console.error('Error loading activity stats:', e)
      } finally {
        this.activityLoaded = true
      }
    },

    async updateAvatar(e) {
      const file = e.target.files[0]
      if (!file) return

      try {
        const user = auth.currentUser
        const path = `avatars/${user.uid}/${Date.now()}_${file.name}`
        const storageRef = sRef(storage, path)
        await uploadBytes(storageRef, file)
        const url = await getDownloadURL(storageRef)

        await updateProfile(user, { photoURL: url })
        this.photoURL = url

        this.snack = { show: true, msg: '✓ Avatar updated' }
      } catch (e) {
        console.error('Avatar update error:', e)
        this.snack = { show: true, msg: '✗ Failed to update avatar' }
      }
    },

    async saveProfile() {
      this.savingProfile = true
      try {
        const user = auth.currentUser

        // Update auth profile
        await updateProfile(user, {
          displayName: this.profileForm.displayName
        })

        // Update Firestore
        await setDoc(doc(db, 'users', user.uid), {
          displayName: this.profileForm.displayName,
          email: this.profileForm.email,
          phone: this.profileForm.phone,
          address: this.profileForm.address,
          updatedAt: new Date()
        }, { merge: true })

        this.displayName = this.profileForm.displayName
        this.snack = { show: true, msg: '✓ Profile updated successfully' }
      } catch (e) {
        console.error('Profile update error:', e)
        this.snack = { show: true, msg: '✗ Failed to update profile' }
      } finally {
        this.savingProfile = false
      }
    },

    checkPasswordStrength() {
      const password = this.passwordForm.newPassword
      if (!password) {
        this.passwordStrength = { percent: 0, label: '', color: '' }
        return
      }

      let strength = 0
      if (password.length >= 8) strength += 25
      if (password.length >= 12) strength += 25
      if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25
      if (/[0-9]/.test(password)) strength += 12.5
      if (/[^a-zA-Z0-9]/.test(password)) strength += 12.5

      let label = 'Weak'
      let color = '#B3554A'
      if (strength >= 75) {
        label = 'Strong'
        color = '#3F9463'
      } else if (strength >= 50) {
        label = 'Medium'
        color = '#C89B4C'
      }

      this.passwordStrength = { percent: strength, label, color }
    },

    async changePassword() {
      this.passwordError = ''

      if (!this.passwordForm.currentPassword || !this.passwordForm.newPassword) {
        this.passwordError = 'Please fill in all fields'
        return
      }

      if (this.passwordForm.newPassword.length < 8) {
        this.passwordError = 'Password must be at least 8 characters'
        return
      }

      if (this.passwordForm.newPassword !== this.passwordForm.confirmPassword) {
        this.passwordError = 'Passwords do not match'
        return
      }

      this.changingPassword = true
      try {
        const user = auth.currentUser
        const credential = EmailAuthProvider.credential(
          user.email,
          this.passwordForm.currentPassword
        )

        await reauthenticateWithCredential(user, credential)
        await updatePassword(user, this.passwordForm.newPassword)

        this.passwordForm = {
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }
        this.snack = { show: true, msg: '✓ Password changed successfully' }
      } catch (e) {
        console.error('Password change error:', e)
        if (e.code === 'auth/wrong-password') {
          this.passwordError = 'Current password is incorrect'
        } else {
          this.passwordError = 'Failed to change password'
        }
      } finally {
        this.changingPassword = false
      }
    }
  }
}
</script>

<style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=Inter:wght@400;500;600;700&display=swap');

  /* ==================== PAGE BACKGROUND ==================== */
  .settings-page {
    min-height: 100vh;
    background: #FBF7EF;
    position: relative;
    overflow-x: hidden;
    font-family: 'Inter', sans-serif;
    padding: 40px 0 80px;
  }

  .settings-page .header-title,
  .settings-page .user-name,
  .settings-page .points-value {
    font-family: 'Fraunces', serif;
    letter-spacing: -0.005em;
  }
  
  .page-grid {
    position: fixed;
    inset: 0;
    background-image:
      linear-gradient(to right, rgba(30, 55, 51, 0.06) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(30, 55, 51, 0.06) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse 70% 60% at 50% 50%, black 20%, transparent 70%);
    animation: grid-flow 30s linear infinite;
    pointer-events: none;
    z-index: 0;
  }
  
  @keyframes grid-flow {
    0% { transform: translate(0, 0); }
    100% { transform: translate(60px, 60px); }
  }
  
  .page-orb {
    position: fixed;
    border-radius: 50%;
    filter: blur(140px);
    opacity: 0.3;
    animation: orb-float 20s ease-in-out infinite;
    pointer-events: none;
    z-index: 0;
  }
  
  .orb-1 {
    width: 800px;
    height: 800px;
    background: radial-gradient(circle, rgba(228,201,137,0.5), transparent);
    top: -300px;
    right: -300px;
  }
  
  .orb-2 {
    width: 700px;
    height: 700px;
    background: radial-gradient(circle, rgba(30,55,51,0.3), transparent);
    bottom: -250px;
    left: -250px;
    animation-delay: 7s;
  }
  
  @keyframes orb-float {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33% { transform: translate(100px, -100px) scale(1.2); }
    66% { transform: translate(-80px, 80px) scale(0.9); }
  }
  
  .settings-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 24px;
    position: relative;
    z-index: 2;
  }
  
  /* ==================== HEADER ==================== */
  .settings-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 32px;
    padding: 32px;
    background: rgba(255, 255, 255, 0.95);
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-radius: 20px;
    backdrop-filter: blur(20px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  }
  
  .header-title {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 32px;
    font-weight: 500;
    color: #26302C;
    margin-bottom: 8px;
    background: linear-gradient(135deg, #C89B4C, #9C7A34);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .header-subtitle {
    font-size: 16px;
    color: #5B6663;
  }
  
  .btn-back {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: white;
    border: 2px solid #E4DCC9;
    border-radius: 10px;
    color: #26302C;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    white-space: nowrap;
  }
  
  .btn-back:hover {
    background: #FBF7EF;
    border-color: #C89B4C;
    box-shadow: 0 4px 20px rgba(200, 155, 76, 0.2);
    transform: translateX(-4px);
  }
  
  /* ==================== GRID ==================== */
  .settings-grid {
    display: grid;
    grid-template-columns: 380px 1fr;
    gap: 24px;
    align-items: start;
  }
  
  /* ==================== SIDEBAR ==================== */
  .sidebar-card {
    position: sticky;
    top: 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
  
  .avatar-section {
    background: rgba(255, 255, 255, 0.95);
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-radius: 20px;
    backdrop-filter: blur(20px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    padding: 32px;
    text-align: center;
  }
  
  .avatar-wrapper {
    position: relative;
    width: 120px;
    height: 120px;
    margin: 0 auto 20px;
  }
  
  .avatar {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background-size: cover;
    background-position: center;
    border: 4px solid rgba(200, 155, 76, 0.2);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  }
  
  .avatar-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 40px;
    font-weight: 900;
    color: #ffffff;
    background: linear-gradient(135deg, #C89B4C, #9C7A34);
  }
  
  .avatar-edit {
    position: absolute;
    bottom: 4px;
    right: 4px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #C89B4C, #9C7A34);
    border: 2px solid #ffffff;
    border-radius: 50%;
    color: #1E3733;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(200, 155, 76, 0.3);
  }
  
  .avatar-edit:hover {
    transform: scale(1.1) rotate(15deg);
    box-shadow: 0 6px 25px rgba(200, 155, 76, 0.5);
  }
  
  .user-name {
    font-size: 22px;
    font-weight: 700;
    color: #26302C;
    margin-bottom: 4px;
  }
  
  .user-email {
    font-size: 14px;
    color: #5B6663;
  }
  
  /* ==================== POINTS CARD ==================== */
  .points-card {
    background: rgba(255, 255, 255, 0.95);
    border: 2px solid rgba(200, 155, 76, 0.3);
    border-radius: 16px;
    backdrop-filter: blur(20px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    padding: 24px;
    display: flex;
    align-items: flex-start;
    gap: 16px;
  }
  
  .points-icon {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #C89B4C, #9C7A34);
    border-radius: 12px;
    color: #1E3733;
    flex-shrink: 0;
    box-shadow: 0 4px 20px rgba(200, 155, 76, 0.3);
  }
  
  .points-label {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #5B6663;
    margin-bottom: 6px;
  }
  
  .points-value {
    font-size: 36px;
    font-weight: 900;
    color: #26302C;
    line-height: 1;
    margin-bottom: 4px;
  }
  
  .points-today {
    font-size: 13px;
    color: #9C7A34;
    font-weight: 600;
    margin-bottom: 12px;
  }
  
  .progress-bar {
    height: 6px;
    background: #EFE8D6;
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 6px;
  }
  
  .progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #C89B4C, #9C7A34);
    border-radius: 3px;
    transition: width 0.5s ease;
  }
  
  .progress-label {
    font-size: 11px;
    color: #5B6663;
  }
  
  /* ==================== ACTIVITY CARD ==================== */
  .activity-card {
    background: rgba(255, 255, 255, 0.95);
    border: 2px solid rgba(200, 155, 76, 0.3);
    border-radius: 16px;
    backdrop-filter: blur(20px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    padding: 24px;
  }
  
  .activity-header {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #26302C;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 16px;
  }
  
  .activity-header svg {
    color: #C89B4C;
  }
  
  .activity-chart {
    margin-bottom: 16px;
  }

  .activity-empty {
    font-size: 12.5px;
    color: #5B6663;
    padding: 10px 0;
    text-align: center;
  }
  
  .activity-stats {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding-top: 16px;
    border-top: 1px solid #EFE8D6;
  }
  
  .stat-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }
  
  .stat-label {
    font-size: 11px;
    color: #5B6663;
  }
  
  .stat-value {
    font-size: 18px;
    font-weight: 700;
    color: #26302C;
  }
  
  .stat-divider {
    color: #E4DCC9;
    font-size: 20px;
  }
  
  /* ==================== CONTENT CARD ==================== */
  .content-card {
    background: rgba(255, 255, 255, 0.95);
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-radius: 20px;
    backdrop-filter: blur(20px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
    overflow: hidden;
  }
  
  /* ==================== TABS ==================== */
  .tabs-header {
    display: flex;
    background: linear-gradient(90deg, #C89B4C, #9C7A34);
    padding: 8px;
    gap: 8px;
  }
  
  .tab-button {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 14px 24px;
    background: transparent;
    border: none;
    border-radius: 10px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
  }
  
  .tab-button:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }
  
  .tab-button.active {
    background: rgba(255, 255, 255, 0.2);
    color: #ffffff;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  }
  
  .tab-content {
    padding: 40px;
  }
  
  .tab-panel {
    animation: fade-in 0.3s ease;
  }
  
  @keyframes fade-in {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  /* ==================== FORMS ==================== */
  .settings-form {
    max-width: 600px;
  }
  
  .form-group {
    margin-bottom: 24px;
  }
  
  .form-group label {
    display: block;
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #5B6663;
    margin-bottom: 8px;
  }
  
  .form-group input,
  .form-group textarea {
    width: 100%;
    padding: 14px 16px;
    background: white;
    border: 2px solid #E4DCC9;
    border-radius: 10px;
    color: #26302C;
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    outline: none;
    transition: all 0.3s ease;
  }
  
  .form-group input:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #FBF7EF;
  }
  
  .form-group input::placeholder,
  .form-group textarea::placeholder {
    color: #93A19C;
  }
  
  .form-group input:focus,
  .form-group textarea:focus {
    background: white;
    border-color: #C89B4C;
    box-shadow: 0 0 0 4px rgba(200, 155, 76, 0.1);
  }
  
  .form-group textarea {
    resize: vertical;
    min-height: 100px;
  }
  
  .form-hint {
    font-size: 12px;
    color: #5B6663;
    margin-top: 6px;
  }

  .forgot-link {
    color: #8A6A2B;
    font-weight: 600;
    text-decoration: none;
  }

  .forgot-link:hover {
    color: #C89B4C;
    text-decoration: underline;
  }
  
  .form-error {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: rgba(179, 85, 74, 0.1);
    border: 1px solid rgba(179, 85, 74, 0.3);
    border-radius: 8px;
    color: #B3554A;
    font-size: 14px;
    margin-bottom: 20px;
  }
  
  .password-input {
    position: relative;
  }
  
  .btn-toggle-password {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    color: #5B6663;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .btn-toggle-password:hover {
    color: #26302C;
  }
  
  .password-strength {
    margin-top: 12px;
  }
  
  .strength-bar {
    height: 4px;
    background: #EFE8D6;
    border-radius: 2px;
    overflow: hidden;
    margin-bottom: 6px;
  }
  
  .strength-fill {
    height: 100%;
    border-radius: 2px;
    transition: all 0.3s ease;
  }
  
  .strength-label {
    font-size: 12px;
    font-weight: 600;
  }
  
  .btn-save {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 14px 32px;
    background: linear-gradient(135deg, #C89B4C, #9C7A34);
    border: none;
    border-radius: 10px;
    color: #1E3733;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 20px rgba(200, 155, 76, 0.3);
  }
  
  .btn-save:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 30px rgba(200, 155, 76, 0.5);
  }
  
  .btn-save:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  /* ==================== SECURITY ==================== */
  .security-notice {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    background: rgba(200, 155, 76, 0.1);
    border: 2px solid rgba(200, 155, 76, 0.3);
    border-radius: 12px;
    margin-bottom: 32px;
  }
  
  .security-notice svg {
    color: #C89B4C;
    flex-shrink: 0;
  }
  
  .notice-title {
    font-size: 16px;
    font-weight: 700;
    color: #26302C;
    margin-bottom: 4px;
  }
  
  .notice-text {
    font-size: 14px;
    color: #5B6663;
  }
  
  /* ==================== TOAST ==================== */
  .toast-notification {
    position: fixed;
    bottom: 24px;
    right: 24px;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 20px;
    background: linear-gradient(135deg, #C89B4C, #9C7A34);
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    color: #1E3733;
    font-size: 14px;
    font-weight: 600;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
    z-index: 1001;
    animation: toast-in 0.3s ease;
  }
  
  @keyframes toast-in {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .toast-notification button {
    background: transparent;
    border: none;
    color: #ffffff;
    font-size: 18px;
    cursor: pointer;
    padding: 0 4px;
    transition: transform 0.2s ease;
  }
  
  .toast-notification button:hover {
    transform: scale(1.2);
  }
  
  /* ==================== RESPONSIVE ==================== */
  @media (max-width: 1200px) {
    .settings-grid {
      grid-template-columns: 1fr;
    }
  
    .sidebar-card {
      position: relative;
      top: 0;
    }
  }
  
  @media (max-width: 768px) {
    .settings-header {
      flex-direction: column;
      text-align: center;
    }
  
    .header-title {
      font-size: 24px;
    }
  
    .tab-content {
      padding: 24px;
    }
  
    .settings-form {
      max-width: 100%;
    }
  
    .activity-stats {
      flex-wrap: wrap;
    }
  }
  </style>